/**
 * DdsPlsContext — IDataService adapter over @aelive/dds-client.
 *
 * Pulls a fixture's full binding context (lineup, teams, result)
 * using the same preset the broker uses on its server-side bind
 * path (Broker/services/caption.service.js:215-220). Hands the
 * snapshot back via the IDataService.getContext() contract so the
 * form library can resolve JSONata expressions against live PLS data.
 *
 * Caching strategy:
 *   - One fetch per adapter instance until the cache is cleared.
 *   - An in-flight fetch coalesces concurrent getContext() calls
 *     (the form-lib resolver awaits multiple branches concurrently;
 *     without this we'd fire a storm of identical DDS requests).
 *   - A failed fetch does NOT poison the cache: the next call retries.
 *
 * Lifecycle: bound to a single fixtureId for its lifetime. The
 * useFixtureContext hook builds a new adapter when the fixture
 * changes, dropping the old instance (and its cache).
 *
 * Live updates: the subscribe() method honours the interface (so
 * the form library can register without crashing) but does not yet
 * wire DDS WS subscriptions — that lands in a follow-up commit.
 */
import { fixtureStaticWithLineupsAndResultPreset } from "@aelive/dds-client";

const EMPTY = Object.freeze({});

export function createDdsPlsContext({ client, fixtureId, competitionId, seasonId }) {
  if (!client) throw new Error("DdsPlsContext: `client` is required");

  let cached = null;        // null until first successful fetch
  let inFlight = null;      // Promise during an in-flight fetch
  const listeners = new Set();

  return {
    /** @returns {Promise<object>} */
    async getContext() {
      if (!fixtureId) return EMPTY;
      if (cached) return cached;
      if (inFlight) return inFlight;

      // The preset needs all three IDs to resolve a complete fixture
      // context (AeroScope/core/useFixtureData.ts:49-53 confirms).
      // Passing only fixture_id returns sparse data.
      const preset = fixtureStaticWithLineupsAndResultPreset({
        competition_id: competitionId,
        season_id: seasonId,
        fixture_id: fixtureId,
      });
      inFlight = client.getStaticData(preset)
        .then((result) => {
          cached = result || EMPTY;
          inFlight = null;
          return cached;
        })
        .catch((err) => {
          inFlight = null;
          throw err;
        });
      return inFlight;
    },

    /**
     * @param {() => void} listener
     * @returns {() => void} unsubscribe
     */
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** Diagnostics hook — surfaces basic transport state. */
    getState() {
      return {
        fixtureId: fixtureId || null,
        hasCache: !!cached,
        inFlight: !!inFlight,
        listeners: listeners.size,
      };
    },
  };
}
